import React, { useEffect } from 'react';

import { AppButton, AppForm, AppSpin } from '@/components/atoms';
import { useCertificationFormLogic } from '@/hooks/useCertificationFormLogic';
import useAppToast from '@/hooks/useAppToast';

import { AddressInfoSection } from './AddressInfoSection';
import CertificationListModal from './CertificationListModal';
import { InsuranceInfoSection } from './InsuranceInfoSection';
import { MedicalAidSection } from './MedicalAidSection';
import PatientCertificationFormBottomBar from './PatientCertificationFormBottomBar';
import PatientDuplicateModal from './PatientDuplicateModal';
import { PatientInfoSection } from './PatientInfoSection';
import {
  FORM_ITEM_LAYOUT,
  INSURANCE_TYPE_KOUKI,
  PATIENT_COST_OPTIONS_KOUKI,
  PATIENT_COST_OPTIONS_PUBLIC,
  RELATIONSHIP_OPTIONS,
  SEX_OPTIONS,
} from './constants';
import type { TPatientCertificationForm } from './schema';

const FORM_ID = 'patient-certification-form';

interface PatientCertificationFormProps {
  initialData?: Partial<TPatientCertificationForm>;
}

export const PatientCertificationForm: React.FC<PatientCertificationFormProps> = ({
  initialData,
}) => {
  const {
    form,
    insuranceType,
    isInsurerSearching,
    isCertificationFetching,
    isFormSubmitting,
    isOverlayLoading,
    handleInsurerNumberChange,
    onSubmitButtonMouseDown,
    handleRelationshipChange,
    handleCostRateDropdownChange,
    handleGetCertification,
    handleSubmitForm,
    showDuplicateModal,
    duplicatePatientIds,
    handleSelectDuplicatePatient,
    handleCreateNewFromDuplicate,
    handleCopyWithHistory,
    handleCloseDuplicateModal,
    showCertificationListModal,
    handleCloseCertificationListModal,
    handleSelectCertification,
    getFormDataForComparison,
  } = useCertificationFormLogic();
  const toast = useAppToast();
  const { control, trigger, getValues, watch, setValue } = form;

  useEffect(() => {
    if (!initialData) return;
    (Object.keys(initialData) as Array<keyof TPatientCertificationForm>).forEach(key => {
      setValue(key, initialData[key] as TPatientCertificationForm[typeof key]);
    });
  }, [initialData, setValue]);

  const handleFinish = async () => {
    const isValid = await trigger();
    if (isValid) {
      handleSubmitForm(getValues() as TPatientCertificationForm);
      return;
    }
    // BD 6.1 button 32: error → toast + inline errors (already shown by trigger()).
    toast.error('入力項目にエラーがあります。');
  };

  const patientCostOptions =
    insuranceType === INSURANCE_TYPE_KOUKI
      ? PATIENT_COST_OPTIONS_KOUKI
      : PATIENT_COST_OPTIONS_PUBLIC;

  return (
    <div className="w-full">
      <div className="relative w-full px-4 pb-4">
        {isOverlayLoading && (
          <div
            className="fixed inset-x-0 top-[7rem] bottom-8 z-40 flex items-center justify-center bg-white/80 md:left-[15rem]"
            aria-hidden="true"
          >
            <AppSpin size="large">
              <div className="min-h-[120px]" />
            </AppSpin>
          </div>
        )}

        <AppForm
          {...FORM_ITEM_LAYOUT}
          onFinish={handleFinish}
          id={FORM_ID}
          layout="horizontal"
          className="patient-certification-form w-full [&_.ant-form-item-control-input-content]:min-w-0 [&_.ant-form-item-row]:flex-col md:[&_.ant-form-item-row]:flex-row"
        >
          <div className="flex justify-end mb-3">
            <AppButton
              type="primary"
              size="large"
              onClick={handleGetCertification}
              className="btn-certification w-full bg-[#465DAA] sm:w-auto"
              disabled={isOverlayLoading || isFormSubmitting}
            >
              資格情報取得
            </AppButton>
          </div>

          <div className="mb-4 border-b border-gray-200" />

          <InsuranceInfoSection
            control={control}
            watch={watch}
            setValue={setValue}
            trigger={trigger}
            insuranceType={insuranceType}
            relationshipOptions={RELATIONSHIP_OPTIONS}
            sexOptions={SEX_OPTIONS}
            patientCostOptions={patientCostOptions}
            onInsurerNumberChange={handleInsurerNumberChange}
            onRelationshipChange={handleRelationshipChange}
            onCostRateDropdownChange={handleCostRateDropdownChange}
            isLoading={isInsurerSearching}
          />

          <MedicalAidSection control={control} watch={watch} />

          <PatientInfoSection
            control={control}
            watch={watch}
            setValue={setValue}
            isLoading={isCertificationFetching}
          />

          <AddressInfoSection control={control} setValue={setValue} watch={watch} />
        </AppForm>

        <PatientCertificationFormBottomBar
          formId={FORM_ID}
          isSubmitting={isFormSubmitting}
          disabled={isOverlayLoading}
          onSubmitMouseDown={onSubmitButtonMouseDown}
        />

        <PatientDuplicateModal
          isOpen={showDuplicateModal}
          onClose={handleCloseDuplicateModal}
          patientIds={duplicatePatientIds}
          onSelectPatient={handleSelectDuplicatePatient}
          onCreateNew={handleCreateNewFromDuplicate}
          onCopyWithHistory={handleCopyWithHistory}
          formDataForComparison={getFormDataForComparison()}
        />

        <CertificationListModal
          isOpen={showCertificationListModal}
          onClose={handleCloseCertificationListModal}
          onSelect={handleSelectCertification}
        />
      </div>
    </div>
  );
};

export default PatientCertificationForm;
